import React, { useState } from "react";

import CalendarIcon from "@uui/Calendar";
import CopyIcon from "@uui/Copy01";
import FileIcon from "@uui/File07";
import { filter, isEmpty, times } from "lodash/fp";

import { Skeleton, ToggleButton, Typography } from "@mui/material";

import IconHeading from "@app/analytics/components/IconHeading";
import ViewToggle from "@app/analytics/components/ViewToggle";
import { DETAILS_BY_TYPE, TOOLTIPS } from "@app/analytics/Pulse/constants";
import type { NoteType, PulseClip } from "@app/analytics/Pulse/types";
import { getModuleCopyContent } from "@app/analytics/Pulse/utils";
import { useAuthenticated } from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";
import { titleize } from "@app/utils/utils";

import CopyButton from "@rui/CopyButton";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Stack, { Column, Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import { Module, NoteListItem } from "./styles";
import { Action, ModuleHeading, SecondaryTextWithIcon } from "../styles";

const LoadingNotes = () => (
  <Stack spacing={1} sx={{ px: 1, width: "100%", pb: 1 }}>
    {times(
      (i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={50}
          sx={{ borderRadius: "8px" }}
        />
      ),
      3
    )}
  </Stack>
);

const EmptyNotes = ({ type }: { type: NoteType }) => {
  const { icon, label, emptyBlurb } = DETAILS_BY_TYPE[type];
  return (
    <Column sx={{ width: "100%", px: 1, flex: 1, pb: 1 }}>
      <EmptyStateContainer>
        <EmptyStateIcon Icon={icon} />
        <EmptyStateTitle>No {label}</EmptyStateTitle>
        <EmptyStateSubtext>{emptyBlurb}</EmptyStateSubtext>
      </EmptyStateContainer>
    </Column>
  );
};

const NoteModule = ({
  type,
  content = [],
  loading,
}: {
  type: NoteType;
  content?: PulseClip[];
  loading: boolean;
}) => {
  const { user } = useAuthenticated();
  const [filterBy, setFilterBy] = useState<"all" | "me">("me");

  const { label, id } = DETAILS_BY_TYPE[type];

  const handleToggleSelection = (
    event: React.SyntheticEvent,
    value: "all" | "me"
  ) => {
    setFilterBy(value);
  };

  const contentToShow =
    type === "action_items" && filterBy === "me"
      ? filter({ ownerUserId: user.id }, content)
      : content;

  return (
    <Module>
      <ModuleHeading variant="apart" spacing={1}>
        <IconHeading type={type}>
          <Row variant="left" sx={{ whiteSpace: "nowrap" }}>
            {titleize(label)}
            <TooltipIcon title={TOOLTIPS[id]} sx={{ ml: 1 }} />
          </Row>
        </IconHeading>
        <Row variant="right" spacing={1}>
          {type === "action_items" && (
            <ViewToggle
              value={filterBy}
              onChange={handleToggleSelection}
              spacing="2px"
            >
              <ToggleButton value="me">For me</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ViewToggle>
          )}
          {!isEmpty(content) && (
            <CopyButton
              copyValue={getModuleCopyContent(type, content)}
              renderIconButton={({ children, ...props }) => (
                <Action {...props}>{children ?? <CopyIcon />}</Action>
              )}
            />
          )}
        </Row>
      </ModuleHeading>
      {loading ? (
        <LoadingNotes />
      ) : isEmpty(contentToShow) ? (
        <EmptyNotes type={type} />
      ) : (
        <Stack width="100%" sx={{ overflowY: "auto" }}>
          {contentToShow.map((item) => (
            <NoteListItem key={item.id} spacing={0.5}>
              <Typography
                variant="bodySecondaryReg"
                sx={{
                  color: "shades.neutral.700",
                  fontFamily: (theme) => theme.typography.fontFamilySecondary,
                }}
              >
                {item.text}
              </Typography>
              <Row spacing={1} sx={{ overflow: "hidden" }}>
                {item.startTime && (
                  <SecondaryTextWithIcon
                    Icon={CalendarIcon}
                    sx={{ overflow: "visible" }}
                  >
                    {item.startTime.toLocaleString({
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </SecondaryTextWithIcon>
                )}
                {item.meetingTitle && (
                  <SecondaryTextWithIcon
                    Icon={FileIcon}
                    title={item.meetingTitle}
                    href={ROUTES.meetingReportById(item.sessionId)}
                  >
                    {item.meetingTitle}
                  </SecondaryTextWithIcon>
                )}
              </Row>
            </NoteListItem>
          ))}
        </Stack>
      )}
    </Module>
  );
};

export default NoteModule;
